import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges, signal, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatChipsModule } from '@angular/material/chips';
import { MatTooltipModule } from '@angular/material/tooltip';
import { firstValueFrom } from 'rxjs';
import {
    CameraEventFetchDto,
    CameraEventFetchStatus,
    CameraEventFetchDtoPaginationResult
} from '../../../../../../../api/gen';
import { CameraEventService } from '../../../../../../../api/gen/api/cameraEvent.service';

@Component({
    selector: 'app-camera-event-fetch-history',
    standalone: true,
    imports: [
        CommonModule,
        MatButtonModule,
        MatIconModule,
        MatProgressBarModule,
        MatChipsModule,
        MatTooltipModule
    ],
    templateUrl: './camera-event-fetch-history.component.html',
    styleUrl: './camera-event-fetch-history.component.scss'
})
export class CameraEventFetchHistoryComponent implements OnInit, OnChanges {
    @Input() cameraId?: string;
    @Input() selectedFetchId?: string;
    @Input() set refreshKey(val: number) { if (val > 0) this.load(); }
    @Output() fetchSelected = new EventEmitter<CameraEventFetchDto>();
    private cameraEventService = inject(CameraEventService);

    fetches = signal<CameraEventFetchDto[]>([]);
    isLoading = signal(false);
    readonly FetchStatus = CameraEventFetchStatus;

    ngOnInit(): void {
        this.load();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['cameraId'] && !changes['cameraId'].firstChange) {
            this.load();
        }
    }

    async load(): Promise<void> {
        if (!this.cameraId) return;
        this.isLoading.set(true);
        try {
            const result = await firstValueFrom<CameraEventFetchDtoPaginationResult>(
                this.cameraEventService.cameraEventsFetchGet(
                    this.cameraId,
                    undefined, // status
                    undefined, // source
                    undefined, // startDate
                    undefined, // endDate
                    0,         // pageIndex
                    20,        // pageSize — show last 20
                    'createdAt',
                    'Desc' as any
                )
            );
            this.fetches.set(result.items ?? []);
        } catch {
            this.fetches.set([]);
        } finally {
            this.isLoading.set(false);
        }
    }

    select(fetch: CameraEventFetchDto): void {
        if (fetch.status === CameraEventFetchStatus.Completed) {
            this.fetchSelected.emit(fetch);
        }
    }

    getStatusColor(status?: CameraEventFetchStatus): string {
        switch (status) {
            case CameraEventFetchStatus.Completed: return 'primary';
            case CameraEventFetchStatus.Failed: return 'warn';
            default: return 'accent';
        }
    }

    getStatusIcon(status?: CameraEventFetchStatus): string {
        switch (status) {
            case CameraEventFetchStatus.Completed: return 'check_circle';
            case CameraEventFetchStatus.Failed: return 'error';
            default: return 'hourglass_empty';
        }
    }

    formatEpoch(epoch?: number): string {
        if (!epoch) return '—';
        return new Date(epoch * 1000).toLocaleString();
    }
}
